<template>
  <ValidationObserver ref="form">
    <b-form @submit.prevent="submitForm">
      <div>
        <label class="font-weight-bold star">Название розыгрыша</label>
        <ValidationProvider name="Название розыгрыша" rules="required|min:2" v-slot="{ errors }">
          <b-form-input
              v-model="giveawayData.name"
              placeholder="Например, Розыгрыш алмазов — эфир 05.09"
              :state="!errors.length ? null : false"
          ></b-form-input>
          <div class="text-danger">{{ errors[0] }}</div>
        </ValidationProvider>
      </div>

      <div class="mt-4">
        <label class="font-weight-bold star">
          Игра
          <span v-if="gameLocked" class="text-muted font-weight-normal ml-1">
            <i class="fa fa-lock"></i> нельзя менять
          </span>
        </label>
        <!-- vid совпадает с полем бэка: ошибку из ответа (game_id) ставим сюда через setServerErrors -->
        <ValidationProvider name="Игра" vid="game_id" rules="required" v-slot="{ errors }">
          <b-form-select
              v-model="giveawayData.game_id"
              :options="gameSelectOptions"
              :disabled="gameLocked"
              :state="!errors.length ? null : false"
          ></b-form-select>
          <div class="text-danger">{{ errors[0] }}</div>
        </ValidationProvider>
        <div class="text-muted mt-1">
          <template v-if="gameLocked">Промокоды розыгрыша уже начали активировать — игра закреплена.</template>
          <template v-else>По игре сайт решает, какой игровой профиль спросить у победителя: для MLBB — MLBB ID и Zone ID, для PUBG MOBILE — PUBG ID.</template>
        </div>
      </div>

      <div class="mt-4">
        <label class="font-weight-bold star">Дата старта</label>
        <ValidationProvider name="Дата старта" rules="required" v-slot="{ errors }">
          <DatePicker
              v-model="giveawayData.starts_at"
              mode="dateTime"
              is24hr
              :popover="{ visibility: 'click' }"
          >
            <template v-slot="{ inputValue, inputEvents }">
              <b-form-input
                  :value="inputValue"
                  v-on="inputEvents"
                  placeholder="Выберите дату и время"
                  :state="!errors.length ? null : false"
              />
            </template>
          </DatePicker>
          <div class="text-danger">{{ errors[0] }}</div>
        </ValidationProvider>
      </div>

      <div class="mt-4">
        <label class="font-weight-bold star">Дата окончания</label>
        <ValidationProvider name="Дата окончания" rules="required" v-slot="{ errors }">
          <DatePicker
              v-model="giveawayData.ends_at"
              mode="dateTime"
              is24hr
              :min-date="giveawayData.starts_at"
              :popover="{ visibility: 'click' }"
          >
            <template v-slot="{ inputValue, inputEvents }">
              <b-form-input
                  :value="inputValue"
                  v-on="inputEvents"
                  placeholder="Выберите дату и время"
                  :state="!errors.length || datesError ? null : false"
              />
            </template>
          </DatePicker>
          <div class="text-danger">{{ errors[0] || datesError }}</div>
        </ValidationProvider>
      </div>

      <div class="mt-4">
        <label class="font-weight-bold star">Количество купонов</label>
        <ValidationProvider name="Количество купонов" rules="required|min_value:1" v-slot="{ errors }">
          <b-form-input
              v-model.number="giveawayData.coupons_count"
              type="number"
              min="1"
              max="1000"
              placeholder="От 1 до 1000"
              :state="!errors.length && !couponsCountError ? null : false"
          ></b-form-input>
          <div class="text-danger">{{ errors[0] || couponsCountError }}</div>
        </ValidationProvider>
        <div class="text-muted mt-1">
          Купоны генерирует бэкенд. При увеличении числа купоны догенерируются, при уменьшении — удаляются лишние
          неактивированные (ниже количества уже активированных опустить нельзя).
        </div>
      </div>

      <div class="d-flex mt-4">
        <b-button type="submit" variant="success" :disabled="isSaving">
          {{ isSaving ? 'Сохраняем...' : submitText }}
        </b-button>
        <b-button variant="secondary" class="ml-2" :to="{ name: 'list_giveaways' }">Отмена</b-button>
      </div>
    </b-form>
  </ValidationObserver>
</template>

<script>
import {DatePicker} from 'v-calendar';
import {ValidationObserver, ValidationProvider} from 'vee-validate';
import {GiveawayAdapterMixin} from '@/mixins/Giveaways/GiveawayAdapterMixin';
import {humanizeErrors} from '@/common/validationMessages';

const MAX_COUPONS_COUNT = 1000

export default {
  name: 'GiveawayForm',
  mixins: [GiveawayAdapterMixin],
  components: {DatePicker, ValidationObserver, ValidationProvider},
  props: {
    value: {
      type: Object,
      required: true
    },
    submitText: {
      type: String,
      default: 'Сохранить'
    },
    isSaving: {
      type: Boolean,
      default: false
    },
    // Игру сменить нельзя (промокоды уже активировали): поле только для чтения.
    gameLocked: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      datesError: '',
      couponsCountError: '',
    };
  },
  computed: {
    giveawayData() {
      return this.value;
    },
    gameSelectOptions() {
      return [{value: null, text: 'Выберите игру', disabled: true}, ...this.giveawayGameOptions];
    }
  },
  methods: {
    validateExtraRules() {
      this.datesError = ''
      this.couponsCountError = ''

      const {starts_at: startsAt, ends_at: endsAt, coupons_count: couponsCount} = this.giveawayData

      if (startsAt && endsAt && new Date(endsAt) <= new Date(startsAt)) {
        this.datesError = 'Дата окончания должна быть позже даты старта'
      }

      if (couponsCount > MAX_COUPONS_COUNT) {
        this.couponsCountError = `Максимум ${MAX_COUPONS_COUNT} купонов`
      }

      return !this.datesError && !this.couponsCountError
    },
    async submitForm() {
      const isValid = await this.$refs.form.validate();
      if (!this.validateExtraRules() || !isValid) {
        return;
      }

      this.$emit('submit', this.giveawayData);
    },
    /**
     * Ошибки валидации из ответа бэка — под своими полями. Сейчас форма
     * знает одно такое поле — game_id (validation.giveaway_game_locked);
     * остальные бэк-ошибки и так видны в тосте.
     */
    setServerErrors(errors) {
      if (this.$refs.form) this.$refs.form.setErrors(humanizeErrors(errors));
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/star.scss";
</style>
